//Angular Core
import { Component, HostListener, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

// Angular Modules
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms'

// Material Modules
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Third Party Modules
import { ToastrModule } from 'ngx-toastr';

// Services
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { ToastrService } from 'ngx-toastr';
import { ValidatorService } from '../../services/validator/validator.service';

// Interfaces
import { SuccessResponse } from '../../interfaces/Success';
import { FormControlStructure } from '../../interfaces/FormControlStructure';
import { InputValidator } from '../../interfaces/GenericInputConfigs';

// Components
import { GenericInput } from '../../components/GenericInput/generic-input.component';

// Menu Configs
import { formControls as formControls } from './menu-configs';

// Validators
import { matchFieldsValidator } from '../../validators/fieldMismatchValidator';

@Component({
  selector: 'ResetPassword',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ToastrModule,
    MatProgressSpinnerModule,
    GenericInput
  ],
  templateUrl: './reset-password-page.component.html',
  styleUrl: './reset-password-page.component.scss'
})
export class ResetPasswordPage {
  form !: FormGroup

  windowSize!: number

  loading: boolean = false;

  formControls: FormControlStructure[] = formControls

  // Dependency Injections
  private authService: AuthenticationService = inject(AuthenticationService)
  private router: Router = inject(Router)
  private toastr: ToastrService = inject(ToastrService)
  private validatorsService: ValidatorService = inject(ValidatorService)

  constructor() {
    this.form = new FormGroup({})

    this.formControls.map((formControl: FormControlStructure) => {
      this.form.addControl(formControl['controlName'], new FormControl(formControl.initialValue, formControl.validators))
    })

    this.form.setValidators(matchFieldsValidator('password', 'confirm_password'))

    this.form.valueChanges.subscribe(() => {
      if(this.form.errors?.['fieldsMismatch']) {
        this.form.get('password')?.setErrors({ fieldsMismatch: true })
        this.form.get('confirm_password')?.setErrors({ fieldsMismatch: true })
      } else {
        this.form.get('password')?.setErrors(null)
        this.form.get('password')?.setErrors(null)
      }
    })
  }

  ngOnInit(): void {
    if(typeof(window) !== 'undefined') {
      this.windowSize = window.innerWidth
    }
  }

  setUserPassword(): void {
    const token: string = this.router.url.split("/")[this.router.url.split("/").length - 1]
    const password: string = this.form.get('password')?.value

    this.authService.resetPassword(password, token)
    .subscribe({
      next: (response: SuccessResponse) => {
        this.loading = false;

        this.toastr.success(response.message)

        this.router.navigate(['/login'])
      },
      error: (errorResponse: HttpErrorResponse) => {
        const { error } = errorResponse

        if(error.errors) {
          let errorKeys: string[] = Object.keys(error.errors)

          errorKeys.forEach((errorKey: string) => {
            error.errors[errorKey].map((error: string) => this.toastr.error(error))
          })
        } else {
          this.toastr.error(errorResponse.error.error)
        }

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  getValidators(controlName: string): InputValidator | undefined {
    return this.validatorsService.getValidators(controlName, this.formControls)
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    let eventTarget = event?.target as Window
    this.windowSize = eventTarget.innerWidth
  }
}
